import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, InternalStandardProps, GridProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DocumentDetails } from "JS/Models";
import { AppTypography } from "JS/React/Components/AppTypography";
import { DocumentDexie } from "JS/Database/Dexie";

const ItemContainer = styled(Grid, {
    shouldForwardProp: (prop) => prop !== "isOffline",
})<{ isOffline?: boolean }>(({ theme, isOffline }) => ({
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1),
    "&:hover": {
        background: theme.palette.grey["200"],
        cursor: "pointer",
    },
    ...(isOffline && {
        color: theme.palette.grey[400],
    }),
}));

const StyledIcon = styled(FontAwesomeIcon, {
    shouldForwardProp: (prop) => prop !== "isOffline",
})<{ isOffline?: boolean }>(({ theme, isOffline }) => ({
    color: isOffline ? theme.palette.grey[400] : theme.palette.common.black,
    fontSize: "35px",
    [theme.breakpoints.down("md")]: {
        fontSize: "22px",
    },
}));

const PdfIcon = styled(FontAwesomeIcon, {
    shouldForwardProp: (prop) => prop !== "isOffline",
})<{ isOffline?: boolean }>(({ theme, isOffline }) => ({
    color: isOffline ? theme.palette.grey[400] : "red",
    margin: theme.spacing(0, 2),
    fontSize: "2rem",
}));

const Description = styled(AppTypography, {
    shouldForwardProp: (prop) => prop !== "isOffline",
})<{ isOffline?: boolean }>(({ theme, isOffline }) => ({
    fontSize: "30px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: isOffline ? theme.palette.grey[400] : theme.palette.grey[600],
    [theme.breakpoints.down("md")]: {
        fontSize: "20px",
    },
    [theme.breakpoints.down("sm")]: {
        fontSize: "18px",
    },
}));

export interface DocumentItemProps extends InternalStandardProps<GridProps> {
    document: DocumentDetails | DocumentDexie;
    isDownloadedDoc: boolean;
    index: number;
    onDocumentClick: (pdfName: string, description?: string) => void;
}

export const OfflineDocumentItem = (props: DocumentItemProps) => {
    const { document, isDownloadedDoc, index, onDocumentClick } = props;
    const downloadedDoc = isDownloadedDoc && (document as DocumentDexie);
    const viewOnlyDoc = !isDownloadedDoc && (document as DocumentDetails);

    return (
        <>
            <ItemContainer
                key={index}
                container
                isOffline={!isDownloadedDoc}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                onClick={() => {
                    isDownloadedDoc &&
                        onDocumentClick(
                            downloadedDoc.id,
                            downloadedDoc.description,
                        );
                }}
            >
                <Grid
                    item
                    sm={10}
                    xs={10}
                    display={"flex"}
                    alignItems={"center"}
                >
                    <PdfIcon
                        isOffline={!isDownloadedDoc}
                        icon={["fas", "file-pdf"]}
                    />
                    <Description isOffline={!isDownloadedDoc} variant="h4">
                        {isDownloadedDoc
                            ? downloadedDoc?.description
                            : viewOnlyDoc?.Description}
                    </Description>
                </Grid>
                <Grid
                    item
                    sm={1}
                    xs={1}
                    display={"flex"}
                    justifyContent={"center"}
                >
                    <StyledIcon
                        isOffline={!isDownloadedDoc}
                        icon={["fas", "chevron-right"]}
                    />
                </Grid>
            </ItemContainer>
        </>
    );
};
